import { GridSortModel } from '@mui/x-data-grid';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useRecoilValueLoadable, useSetRecoilState } from 'recoil';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import {
  endpointEnum,
  makeAPIRequest,
} from '../../../services/rest/makeAPIRequest';
import dataTableScss from '../../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../../modules/Manage/pages/queues-table/DataTable';
import {
  EnhancedTableToolbar,
  refreshDurations,
  ToolbarRefreshButton,
} from '../../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { ModalWithStyles } from './ModalWithStyles';

export const EventTrailModal = (props: {
  handleClose: () => void;
  show: boolean;
  objectType: string; //including ambiguous object type string, for possible plans to incorporate this into queues, etc
  businessId: number;
  internalCallId: string;
  externalCallId: string;
  timezone: string | undefined;
  conferenceName: string | undefined;
}) => {
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);

  //pull the event trail data for this object
  const eventTrailLoadable = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_AUDIT_EVENTS,
      method: 'POST',
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_AUDIT_EVENTS,
        businessId: props.businessId,
        externalId: `${props.externalCallId}`,
        conferenceName:
          props.conferenceName === '' ? '-' : props.conferenceName, //handle empty conferenceName value
      }),
    })
  );

  const refreshToggleForEventTrail = useSetRecoilState(
    refetchToggle(endpointEnum.GET_AUDIT_EVENTS)
  );

  const rowsCached = useRef([]);

  /**
   * Transform by adding id
   * NOTE: must use useMemo so rows do not get updated on re-render
   */
  rowsCached.current = React.useMemo(
    () =>
      eventTrailLoadable.state !== 'loading' &&
      eventTrailLoadable.contents?.response?.data !== ''
        ? eventTrailLoadable.contents?.response?.data?.map((row: any) => ({
            ...row,
            id: row.auditEventCallId,
            createdDateTz: DateTime.fromMillis(Date.parse(row.createdDate), {
              //transform created date to this business's timezone
              zone: props.timezone ?? 'Pacific',
            }).toLocaleString(DateTime.DATETIME_SHORT),
            // conferenceNameString: props.conferenceName ?? ''
            // messageConference: row.message + (row.conferenceName ? ' (Conference Name: ' + row.conferenceName + ')': '')
          }))
        : eventTrailLoadable.state !== 'loading' &&
          eventTrailLoadable.contents?.response?.data !== '' //handle empty string data response
        ? []
        : rowsCached?.current ?? [],
    [eventTrailLoadable]
  );

  const total = rowsCached.current?.length ?? 0;
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'auditEventCallId', //default sort by event call id
      sort: 'asc',
    },
  ]);

  const handlePageChange = (page: number, details?: any) => {
    setPage(page);
  };
  const handlePageSizeChange = (pageSize: number, details?: any) => {
    setPageSize(pageSize);
  };

  const handleSortModelChange = (params: GridSortModel) => {
    if (params !== sortModel) {
      setSortModel(params);
    }
  };

  /**
   * Create columns for DataGrid renderer
   * use useMemo to prevent unneeded rerenderings
   */
  const columns = useMemo<IColDef[]>(
    () => [
      {
        field: 'auditEventCallId',
        headerName: 'Event ID',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'createdDateTz',
        headerName: 'Date/Time',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'eventType',
        headerName: 'Event Name',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
      {
        field: 'message',
        headerName: 'Event Description',
        headerClassName: dataTableScss.fullWidth,
        cellClassName: dataTableScss.fullWidth,
      },
    ],
    [rowsCached] //dependencies for this hook to run
  );
  const ToolbarComponent = useCallback(
    (props) => (
      <EnhancedTableToolbar
        dataType="EventTrailLive"
        {...props}
        // customSearchField={<></>}
        csvData={null}
        csvExport={false}
        rightComponent={
          <ToolbarRefreshButton
            id="EventTrailLive"
            duration={refreshDurations.dur30sec}
            onClickForRefresh={() => {
              refreshToggleForEventTrail({});
            }}
            isLoading={false}
            autoRefresh={false}
            showRefreshDropDownOptions={false}
          />
        }
      />
    ),
    []
  );
  //pretty much return a modal containing a DataGrid table
  return (
    <ModalWithStyles
      show={props.show}
      aria-labelledby="example-modal-sizes-title-sm"
      title={`Event Trail - Call ID: ${props.internalCallId}`}
      onHide={() => {
        props.handleClose();
      }}
      isLoading={eventTrailLoadable?.state !== 'hasValue'}
      size="md"
      hideFooter={true}
    >
      <DataTable
        rows={rowsCached?.current ?? []}
        columns={columns}
        rowCount={total}
        page={page}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        paginationMode="client"
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        loading={eventTrailLoadable.state === 'loading'}
        pagination
        ToolbarComponent={ToolbarComponent}
      />
    </ModalWithStyles>
  );
};
